import React from 'react';
import styled from 'styled-components';
import { Button } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Campaign, CampaignRecipientEstimate, CampaignStatus, WhatsAppTemplateCatalogItem } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { CampaignAudienceSummary } from './CampaignAudienceSummary';
import { CampaignPreviewPanel } from './CampaignPreviewPanel';
import { formatCampaignDateTime, getCampaignStatusTranslationKey } from './campaignsUtils';

const ReviewGrid = styled.div`
    display: grid;
    gap: var(--spacer-M);
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-M);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: white;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const SectionLabel = styled.div`
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-muted);
    margin-bottom: var(--spacer-XS);
`;

const ErrorList = styled.ul`
    margin: 0;
    padding-left: 1.2rem;
    color: var(--danger-color);
    font-size: 0.88rem;
`;

const PaymentCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-M);
    border-radius: var(--spacer-XS);
    border: var(--border-thin) solid #d1fae5;
    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
`;

const PaymentHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
`;

const PaymentTitle = styled.div`
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #059669;
`;

const PaymentRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: var(--spacer-S);
`;

const PriceAmount = styled.div`
    font-size: 2rem;
    font-weight: 800;
    color: #065f46;
    line-height: 1;
`;

const PriceCurrency = styled.div`
    font-size: 1rem;
    font-weight: 600;
    color: #065f46;
`;

const PriceDetail = styled.div`
    font-size: 0.82rem;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const PriceNote = styled.div`
    font-size: 0.78rem;
    color: #6b7280;
    font-style: italic;
`;

const PaymentDoneBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--success-bg);
    border: var(--border-thin) solid var(--success-border);
    color: var(--success-color);
    font-weight: 600;
    font-size: 0.88rem;
    border-radius: 20px;
    padding: 4px var(--spacer-S);
`;

const Divider = styled.div`
    height: var(--border-thin);
    background: #d1fae5;
`;

const StatusCard = styled.div<{ $variant: 'sending' | 'sent' | 'partial' | 'failed' }>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-M);
    border-radius: var(--spacer-XS);
    border: var(--border-thin) solid ${({ $variant }) =>
        $variant === 'sent' ? 'var(--success-border)' : 'var(--warning-border)'};
    background: ${({ $variant }) =>
        $variant === 'sent' ? 'var(--success-bg)' : 'var(--warning-bg)'};
`;

const StatusCardTitle = styled.div<{ $variant: 'sending' | 'sent' | 'partial' | 'failed' }>`
    font-weight: 700;
    font-size: 1rem;
    color: ${({ $variant }) =>
        $variant === 'sent' ? 'var(--success-color)' : 'var(--text-body)'};
`;

const StatusCardNote = styled.div`
    font-size: 0.88rem;
    color: var(--text-body);
    line-height: 1.5;
`;

const PreviouslyTriggeredCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    border-radius: var(--spacer-XS);
    border: var(--border-thin) solid var(--appLigthGrey);
    background: var(--bg-light);
`;

const CooldownBanner = styled.div`
    padding: var(--spacer-S);
    border-radius: var(--spacer-XS);
    border: var(--border-thin) solid var(--warning-border);
    background: var(--warning-bg);
    color: var(--text-body);
    font-weight: 600;
    font-size: 0.9rem;
`;

const TERMINAL_STATUSES = [CampaignStatus.Sending, CampaignStatus.Sent, CampaignStatus.PartiallyFailed, CampaignStatus.Failed];

const getStatusVariant = (status: CampaignStatus): 'sending' | 'sent' | 'partial' | 'failed' => {
    if (status === CampaignStatus.Sent) return 'sent';
    if (status === CampaignStatus.Sending) return 'sending';
    if (status === CampaignStatus.PartiallyFailed) return 'partial';
    return 'failed';
};

export interface CampaignReviewStepProps {
    campaign: Campaign;
    estimate?: CampaignRecipientEstimate;
    whatsAppTemplate?: WhatsAppTemplateCatalogItem;
    readinessIssues: TK[];
    paymentDone: boolean;
    isOnCooldown: boolean;
    cooldownEndsAt?: string;
    onViewAnalytics: () => void;
}

export const CampaignReviewStep: React.FC<CampaignReviewStepProps> = ({
    campaign,
    estimate,
    whatsAppTemplate,
    readinessIssues,
    paymentDone,
    isOnCooldown,
    cooldownEndsAt,
    onViewAnalytics,
}) => {
    const t = useTranslations();
    const isTerminal = TERMINAL_STATUSES.includes(campaign.status);
    const variant = isTerminal ? getStatusVariant(campaign.status) : undefined;
    const hasPreviousSend = !!campaign.lastTriggeredAt;

    const displayedIssues = readinessIssues.filter(issue => issue !== TK.campaignAlreadySentCannotSend);

    return (
        <ReviewGrid>
            {/* Terminal status banner — replaces error for already-sent */}
            {isTerminal && variant && (
                <StatusCard $variant={variant}>
                    <StatusCardTitle $variant={variant}>
                        {t(getCampaignStatusTranslationKey(campaign.status))}
                    </StatusCardTitle>
                    {variant === 'sending' && (
                        <StatusCardNote>{t(TK.campaignSendingNote)}</StatusCardNote>
                    )}
                    <StatusCardNote>{t(TK.campaignSentAnalyticsHint)}</StatusCardNote>
                    <Button
                        variant="contained"
                        color={variant === 'sent' ? 'success' : 'warning'}
                        startIcon={<BarChartIcon />}
                        onClick={onViewAnalytics}
                        fullWidth
                        size="large"
                        sx={{ fontWeight: 700, fontSize: '1rem', mt: 0.5 }}
                    >
                        {t(TK.campaignViewAnalytics)}
                    </Button>
                </StatusCard>
            )}

            {/* Previously triggered (non-terminal) — e.g. editing a re-draft */}
            {!isTerminal && hasPreviousSend && (
                <PreviouslyTriggeredCard>
                    <span style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>
                        {t(TK.campaignPreviouslySentHint)}
                    </span>
                    <Button
                        variant="outlined"
                        startIcon={<BarChartIcon />}
                        onClick={onViewAnalytics}
                        size="small"
                    >
                        {t(TK.campaignViewAnalytics)}
                    </Button>
                </PreviouslyTriggeredCard>
            )}

            {/* Cooldown active banner */}
            {isOnCooldown && (
                <CooldownBanner>
                    {t(TK.campaignAnalyticsReTriggerNotEligible)}
                    {cooldownEndsAt && (
                        <> — {t(TK.campaignAnalyticsEligibleFrom)} {formatCampaignDateTime(cooldownEndsAt)}</>
                    )}
                </CooldownBanner>
            )}

            {/* Audience */}
            <Card>
                <SectionLabel>{t(TK.campaignAudienceIncluded)}</SectionLabel>
                <CampaignAudienceSummary estimate={estimate} />
            </Card>

            {/* Preview */}
            <Card>
                <SectionLabel>{t(TK.preview)}</SectionLabel>
                <CampaignPreviewPanel campaign={campaign} whatsappTemplate={whatsAppTemplate} />
            </Card>

            {/* Readiness errors (excluding "already sent" which is handled by the banner) */}
            {displayedIssues.length > 0 && (
                <ErrorList>
                    {displayedIssues.map(issue => <li key={issue}>{t(issue)}</li>)}
                </ErrorList>
            )}

            {/* Pricing — at the bottom, prominent */}
            {estimate?.requiresPayment && (
                <PaymentCard>
                    <PaymentHeader>
                        <PaymentTitle>{t(TK.campaignPayRequired)}</PaymentTitle>
                        {paymentDone && (
                            <PaymentDoneBadge>
                                <span>✓</span>
                                {t(TK.campaignPaySuccess)}
                            </PaymentDoneBadge>
                        )}
                    </PaymentHeader>
                    <Divider />
                    <PaymentRow>
                        <PriceCurrency>€</PriceCurrency>
                        <PriceAmount>{estimate.pricingTotal.toFixed(2)}</PriceAmount>
                    </PaymentRow>
                    <PriceDetail>
                        <span>{estimate.totalIncluded} {t(TK.campaignPricingRecipients).toLowerCase()}</span>
                    </PriceDetail>
                    {estimate.pricingTotal < 0.50 && (
                        <PriceNote>{t(TK.campaignMinPaymentDisclaimer)}</PriceNote>
                    )}
                </PaymentCard>
            )}
        </ReviewGrid>
    );
};
